import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

interface UsePreloadedImagesOptions {
  /** Max image URLs to preload */
  maxCount?: number;
  /** Minimum ms before reporting ready */
  minReadyMs?: number;
  /** Show grid anyway after this many ms. */
  timeoutMs?: number;
  /** Once ready, stay ready for the lifetime of the component. */
  latch?: boolean;
}

export function usePreloadedImages(
  urls: string[],
  {
    maxCount = 20,
    minReadyMs = 250,
    timeoutMs = 2500,
    latch = false,
  }: UsePreloadedImagesOptions = {}
) {
  const batch = useMemo(
    () => [...new Set(urls)].slice(0, maxCount),
    [urls, maxCount]
  );

  const batchKey = useMemo(() => batch.join('\0'), [batch]);
  const prevBatchKeyRef = useRef(batchKey);
  const latchedRef = useRef(latch && batch.length === 0);

  const [ready, setReady] = useState(() => batch.length === 0);

  useLayoutEffect(() => {
    if (latch && latchedRef.current) {
      return;
    }
    if (prevBatchKeyRef.current === batchKey) {
      return;
    }
    prevBatchKeyRef.current = batchKey;
    setReady(batch.length === 0);
  }, [latch, batchKey, batch.length]);

  useEffect(() => {
    if (batch.length === 0) {
      return;
    }

    const delay = (ms: number) =>
      new Promise<void>((resolve) => {
        window.setTimeout(resolve, ms);
      });

    const loadImage = (src: string) =>
      new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
      });

    let cancelled = false;
    const preload = Promise.all(batch.map(loadImage));
    const gates = [Promise.race([preload, delay(timeoutMs)])];

    if (minReadyMs > 0) {
      gates.push(delay(minReadyMs));
    }

    void Promise.all(gates).then(() => {
      if (!cancelled) {
        if (latch) {
          latchedRef.current = true;
        }
        setReady(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [batch, timeoutMs, minReadyMs, latch]);

  return latch && latchedRef.current ? true : ready;
}
